import { Component, OnInit, ChangeDetectionStrategy, inject, DestroyRef, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AvailableLanguages, LanguageService } from '@shared/services';
import { Observable, tap, firstValueFrom } from 'rxjs';
import { first, filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconPath } from '../../../app.constants';
import { ThemeService } from '../../../services/theme.service';
import { ProfileService } from '@shared/services/api/profile.service';
import { AuthenticationService } from '@shared/services/authentication.service';
import { NotificationsService } from '@shared/services/api/notifications.service';
import { Notification } from '@shared/entities/notification';
import { Roles } from '@shared/models/types';
import { Profile } from '@shared/entities/profile';
import { environment } from '@environments/environment';
import { staggerAnimation } from '@shared/animations';

interface MenuItem {
  route: string;
  permission: string;
  iconLight: string;
  iconDark: string;
  iconSelected: string;
  label: string;
  condition: boolean | ((url: string) => boolean);
  roles?: string[];
  auth?: boolean;
  blank?: boolean;
}

const MENU: MenuItem[] = [
  {
    route: '/home',
    permission: 'home',
    iconLight: 'home-light',
    iconDark: 'home-dark',
    iconSelected: 'home-white',
    label: 'SIDEBAR.HOME',
    condition: true,
    roles: ['CUSTOMER', 'CUSTOMER_PARTNER']
  },
  {
    route: '/final-users',
    permission: 'home',
    iconLight: 'home-light',
    iconDark: 'home-dark',
    iconSelected: 'home-white',
    label: 'SIDEBAR.HOME',
    condition: true,
    roles: ['PARTNER']
  },
  {
    route: '/repository',
    permission: 'repository',
    iconLight: 'light/repository',
    iconDark: 'dark/repository',
    iconSelected: 'info-circle-white',
    label: 'SIDEBAR.REPOSITORY',
    condition: true
  },
  {
    route: '/analytics',
    permission: 'analytics',
    iconLight: 'light/analytics',
    iconDark: 'dark/analytics',
    iconSelected: 'analytics-white',
    label: 'SIDEBAR.ANALYTICS',
    condition: true,
    roles: ['CUSTOMER', 'CUSTOMER_PARTNER']
  },
  {
    route: '/ticket',
    permission: 'ticket',
    iconLight: 'ticket-light',
    iconDark: 'ticket-dark',
    iconSelected: 'ticket-white',
    label: 'SIDEBAR.TICKET',
    condition: true
  },
  {
    route: '/request',
    permission: 'request',
    iconLight: 'info-circle-light',
    iconDark: 'info-circle-dark',
    iconSelected: 'info-circle-white',
    label: 'SIDEBAR.REQUEST',
    condition: true
  },
  {
    route: '/learning',
    permission: 'learning',
    iconLight: 'light/learning',
    iconDark: 'dark/learning',
    iconSelected: 'info-circle-white',
    label: 'SIDEBAR.LEARNING',
    condition: true
  },
  {
    route: 'https://store.icamonline.eu/',
    permission: 'shopify',
    iconLight: 'shopify-light',
    iconDark: 'shopify-dark',
    iconSelected: 'info-circle-white',
    label: 'SIDEBAR.SHOPIFY',
    condition: true,
    auth: false,
    blank: true
  },
  {
    route: environment.knowledgeBaseUrl,
    permission: 'knowledge-base',
    iconLight: 'knowledge-base-light',
    iconDark: 'knowledge-base-dark',
    iconSelected: 'knowledge-base-white',
    label: 'SIDEBAR.KNOWLEDGE_BASE',
    condition: true,
    blank: true
  },
  {
    route: '/banner',
    permission: 'admin',
    iconLight: 'banner-light',
    iconDark: 'banner-dark',
    iconSelected: 'banner-white',
    label: 'SIDEBAR.BANNER',
    condition: true
  },
  {
    route: '/user',
    permission: 'user',
    iconLight: 'user-light',
    iconDark: 'user-dark',
    iconSelected: 'user-white',
    label: 'SIDEBAR.USER',
    condition: true
  }
];

function getFilteredMenu(items: MenuItem[], profile?: Profile): MenuItem[] {
  if (!profile) return [];
  if (profile.role === Roles.ADMIN) {
    return items.filter((item) => item.route !== '/final-users');
  }
  return items.filter((item) => {
    if (item.route === '/user') return false;

    const hasRequiredRole = !item.roles || (profile.role && item.roles.includes(profile.role));

    const hasRequiredPermission =
      !item.permission ||
      (item.permission === 'admin' && profile.role === Roles.ADMIN) ||
      (profile.permissions && Array.isArray(profile.permissions) && profile.permissions.includes(item.permission));

    return hasRequiredPermission && hasRequiredRole;
  });
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [staggerAnimation]
})
export class SidebarComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);

  currentLanguage$: Observable<string>;
  availableLanguages: AvailableLanguages[];
  isLight?: boolean;
  isSidebarVisible = false;
  notifications: Notification[] = [];
  menuItems: MenuItem[] = []; // Initialized in ngOnInit or constructor properly

  userImage?: string | null;
  dropdownOpen = false;

  constructor(
    private _router: Router,
    private _themeService: ThemeService,
    private _languageService: LanguageService,
    private _profileService: ProfileService,
    private _authenticationService: AuthenticationService,
    private _notificationsService: NotificationsService
  ) {
    this.currentLanguage$ = this._languageService.current$;
    this.availableLanguages = this._languageService.getAvailableLanguages();
  }

  ngOnInit(): void {
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        this._cdr.markForCheck();
      });

    this._themeService.isLight$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((light) => {
          this.isLight = light;
          this._cdr.markForCheck();
        })
      )
      .subscribe();

    this._profileService.profile$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((profile) => {
          this.userImage = profile.photo;
          this.menuItems = getFilteredMenu(MENU, profile);
          this._cdr.markForCheck();
        })
      )
      .subscribe();

    this._notificationsService
      .getNotifications()
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((notifs) => {
          this.notifications = notifs;
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  getIconPath(iconName: string, isDefault?: boolean): string {
    if (isDefault) return `${IconPath}${iconName}.svg`;
    return `${IconPath}${this.isLight ? 'light' : 'dark'}/${iconName}.svg`;
  }

  selectLanguage(lang: string): void {
    this._languageService.setCurrent(lang);
  }

  toggleTheme(selectedTheme: 'light' | 'dark'): void {
    this._themeService.setTheme(selectedTheme);
  }

  isLightTheme(): boolean {
    return this._themeService.isLight();
  }

  isDarkTheme(): boolean {
    return this._themeService.isDark();
  }

  isRouteActive(route: string): boolean {
    const currentUrl = this._router.url.split('?')[0];
    return currentUrl === route || currentUrl.startsWith(route + '/');
  }

  async navigate(url: string, auth?: boolean, blank?: boolean): Promise<void> {
    this.isSidebarVisible = false;
    if (url === environment.knowledgeBaseUrl && window.IcamKbChat) {
      // Opens the full KB site in a new tab, pre-authenticated via the widget's own
      // SSO form-post (POST /auth/sso with a fresh token) - not the embedded chat panel.
      window.IcamKbChat.openKnowledgeBase();
      return;
    }
    if (auth) {
      const idTokenResult = await firstValueFrom(this._authenticationService.idTokenResult);
      // Defensive: idTokenResult may be null or not have token
      const token =
        idTokenResult && typeof idTokenResult === 'object' && 'token' in idTokenResult
          ? (idTokenResult as any).token
          : '';
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}_token=${encodeURIComponent(token)}`;
    }
    if (url.startsWith('http')) {
      if (blank) {
        window.open(url, '_blank');
      } else {
        document.location.href = url;
      }
    } else {
      this._router.navigateByUrl(url);
    }
  }

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
  }

  toggleSidebar(): void {
    this.isSidebarVisible = !this.isSidebarVisible;
  }

  closeDropdown(): void {
    this.dropdownOpen = false;
  }

  closeSidebar(): void {
    this.isSidebarVisible = false;
  }

  onLogout(): void {
    this._authenticationService
      .logout()
      .pipe(first())
      .subscribe(() => this._router.navigate(['/login']));
  }
}
